import { useEffect, useRef } from "react";
import { useDebouncedCallback } from "use-debounce";
import { serializeByKey } from "./serializeByKey";

/** Coalesces a run of field changes into one request. */
const SAVE_DELAY = 600;

/** Anything the editor holds as a row: a session id is what tells a row that
 *  already exists from one that was just added. */
interface IdentifiedRow {
  id: string;
}

export interface AssetListAutosaveOptions<TRow extends IdentifiedRow, TWire> {
  /** The rows as the asset held them when the form mounted. */
  initialRows: TRow[];
  /**
   * What is being written — the asset id. Writes sharing it are run one at a
   * time, across remounts as well as within one: these cards unmount on every
   * rail switch and flush a write on the way out, so a request is routinely
   * open with no card mounted, and the card that comes back would otherwise
   * write straight over it.
   */
  saveKey: string;
  /**
   * What to send for one row of the form, given the version last accepted by
   * the backend — `undefined` for a row that has never been saved. Returning
   * `null` leaves the row out of the payload entirely.
   *
   * This is the only place the cards genuinely differ, and it is where the
   * "a cleared row keeps its saved text" and "a new row waits until it is
   * ready" rules live.
   */
  resolveRow: (row: Partial<TRow>, saved: TRow | undefined) => TRow | null;
  /** The rows as the asset stores them. */
  toWire: (rows: TRow[]) => TWire[];
  /** Whether two payloads carry the same content — a save is skipped if so. */
  isSameWire: (a: TWire[], b: TWire[]) => boolean;
  /** Replace the whole asset. Rejects on failure so the form can be put back. */
  save: (wire: TWire[]) => Promise<void>;
  onSaveFailed: () => void;
  /** Put the form back to the last state the backend accepted. */
  revert: (rows: TRow[]) => void;
}

/**
 * The autosave shared by the add-on content editors (Quick recall's deck,
 * Quiz's question bank): debounced, skipped when nothing content-bearing
 * changed, flushed on unmount, and reverted on failure unless the form has
 * moved on since.
 *
 * Each asset is replaced whole — there is no per-row endpoint — so the payload
 * is rebuilt from the form on every save.
 *
 * Returns the handler for the form's `onValuesChange`: pass it the list's
 * rows. It is deliberately not the debounced function itself, because the
 * change has to be counted the moment it happens (see `formVersionRef`),
 * not 600 ms later.
 */
export const useAssetListAutosave = <TRow extends IdentifiedRow, TWire>({
  initialRows,
  saveKey,
  resolveRow,
  toWire,
  isSameWire,
  save,
  onSaveFailed,
  revert,
}: AssetListAutosaveOptions<TRow, TWire>) => {
  // The rows last known to be on the backend — what a failed save reverts to,
  // and whose ids mark a row as "existing" rather than freshly added.
  const saved = useRef<TRow[]>(initialRows);
  const mounted = useRef(true);
  // Bumped on every change to the form, not on every save. A failed save
  // reverts to the last state the backend accepted, which is only safe while
  // nothing has been typed since it started: a request outlives the keystrokes
  // made during it, and reverting would wipe them. Counting saves instead of
  // changes missed that window — the next save is still behind the debounce
  // when the failure lands, so the guard passed and the typing was lost.
  const formVersionRef = useRef(0);

  // The callbacks are closed over directly rather than held in a ref of our
  // own: `useDebouncedCallback` keeps the latest one in an effect, so a call
  // that fires 600 ms later still runs against this render's `assetId`, save
  // hook and translations.
  //
  // Never rejects: a failure is reported and reverted here, and `serializeByKey`
  // wants tasks that settle.
  const runSave = async (
    rows: Partial<TRow>[],
    version: number
  ): Promise<void> => {
    const savedById = new Map(saved.current.map((row) => [row.id, row]));
    const kept = rows.flatMap((row) => {
      const previous = row.id ? savedById.get(row.id) : undefined;
      const resolved = resolveRow(row, previous);
      return resolved ? [resolved] : [];
    });
    const next = toWire(kept);
    // Nothing content-bearing changed (a blank row was added, or a field was
    // typed back to what it was): no request to make.
    if (isSameWire(next, toWire(saved.current))) return;
    const previous = saved.current;
    try {
      await save(next);
      saved.current = kept;
    } catch {
      onSaveFailed();
      // Detached form: nothing to revert, and setFieldsValue would warn.
      // Anything typed since this save started is newer than what it would put
      // back, so leave the form alone and let the next save carry it.
      if (mounted.current && formVersionRef.current === version) {
        // Rows the backend never accepted aren't in `previous`, so restoring
        // it alone would delete them — and the user typed them. Put the saved
        // rows back (which also restores any that were removed) and leave the
        // unsaved ones where they are.
        const previousIds = new Set(previous.map((row) => row.id));
        const unsaved = rows.filter(
          (row) => row.id && !previousIds.has(row.id)
        ) as TRow[];
        revert([...previous, ...unsaved]);
      }
    }
  };

  // The version is captured here, as the save is queued, not when it reaches
  // the front: it belongs to the rows being sent, and the revert guard below
  // compares against what those rows reflect.
  const persist = useDebouncedCallback((rows: Partial<TRow>[]) => {
    const version = formVersionRef.current;
    void serializeByKey(saveKey, () => runSave(rows, version));
  }, SAVE_DELAY);

  // use-debounce drops a pending call on unmount, and these cards unmount on
  // every rail switch — flush, or the last change silently never saves. Set on
  // the way in too: a remount reuses the ref, and a stale `false` would leave
  // the revert above permanently dead.
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      void persist.flush();
    };
  }, [persist]);

  return (rows: Partial<TRow>[]) => {
    formVersionRef.current += 1;
    void persist(rows);
  };
};
